import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
 Card,
 CardContent,
 CardDescription,
 CardHeader,
 CardTitle,
} from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { UrlParser } from './UrlParser';
import { EditableFields } from './EditableFields';
import { useJobProfileStore } from '@/stores/jobProfileStore';
import type { ParsedJobData, CreateJobProfileData } from '@/types/jobProfile';

interface JobProfileFormProps {
 onSuccess?: () => void;
}

export function JobProfileForm({ onSuccess }: JobProfileFormProps) {
 const [step, setStep] = useState<'url' | 'edit' | 'save'>('url');
 const [parsedData, setParsedData] = useState<ParsedJobData | null>(null);
 const [formData, setFormData] = useState<CreateJobProfileData | null>(null);
 const [jobUrl, setJobUrl] = useState('');
 const { createProfile, isLoading } = useJobProfileStore();
 const { toast } = useToast();
 const navigate = useNavigate();

 const handleUrlParsed = (data: ParsedJobData, url: string) => {
  setParsedData(data);
  setJobUrl(url);
  setStep('edit');
 };

 const handleFieldsEdited = (data: CreateJobProfileData) => {
  setFormData(data);
  setStep('save');
 };

 const handleSaveProfile = async () => {
  if (!formData) return;
  try {
   await createProfile({ ...formData, jobUrl: jobUrl || undefined });
   toast({
    title: 'Success!',
    description: 'Job profile created successfully.',
   });
   if (onSuccess) onSuccess();
   else navigate('/profiles');
  } catch (error) {
   console.error('Error creating job profile:', error);
  }
 };

 const handleStartOver = () => {
  setStep('url');
  setParsedData(null);
  setFormData(null);
  setJobUrl('');
 };

 return (
  <div className='max-w-4xl mx-auto space-y-6'>
   <div className='flex items-center justify-center space-x-4 mb-8 font-mono text-sm tracking-widest'>
    <div
     className={`flex items-center ${
      step === 'url' ? 'text-primary font-bold' : 'text-muted-foreground'
     }`}
    >
     <span>[01]_PARSE_JOB</span>
    </div>
    <span className='text-muted-foreground/30'>&gt;&gt;</span>
    <div
     className={`flex items-center ${
      step === 'edit' ? 'text-primary font-bold' : 'text-muted-foreground'
     }`}
    >
     <span>[02]_EDIT_DETAILS</span>
    </div>
    <span className='text-muted-foreground/30'>&gt;&gt;</span>
    <div
     className={`flex items-center ${
      step === 'save' ? 'text-primary font-bold' : 'text-muted-foreground'
     }`}
    >
     <span>[03]_SAVE_PROFILE</span>
    </div>
   </div>

   {step === 'url' && (
    <Card className='rounded-none border border-border bg-black/40 backdrop-blur-sm'>
     <CardHeader>
      <CardTitle className='font-mono uppercase tracking-wider text-xl'>
       INITIATE_JOB_PARSING
      </CardTitle>
      <CardDescription className='font-mono text-xs'>
       &gt; AWAITING_INPUT: PASTE_JOB_URL
      </CardDescription>
     </CardHeader>
     <CardContent>
      <UrlParser onParsed={handleUrlParsed} />
     </CardContent>
    </Card>
   )}

   {step === 'edit' && parsedData && (
    <Card className='rounded-none border border-border bg-black/40 backdrop-blur-sm'>
     <CardHeader>
      <CardTitle className='font-mono uppercase tracking-wider text-xl'>
       REVIEW_EXTRACTED_DATA
      </CardTitle>
      <CardDescription className='font-mono text-xs'>
       &gt; VERIFY_INTEGRITY: EDIT_IF_REQUIRED
      </CardDescription>
     </CardHeader>
     <CardContent>
      <EditableFields
       parsedData={parsedData}
       onSave={handleFieldsEdited}
       onBack={handleStartOver}
      />
     </CardContent>
    </Card>
   )}

   {step === 'save' && formData && (
    <Card className='rounded-none border border-border bg-black/40 backdrop-blur-sm'>
     <CardHeader>
      <CardTitle className='font-mono uppercase tracking-wider text-xl'>
       CONFIRM_PROFILE_CREATION
      </CardTitle>
      <CardDescription className='font-mono text-xs'>
       &gt; FINAL_CHECK: COMMIT_TO_DATABASE
      </CardDescription>
     </CardHeader>
     <CardContent className='space-y-6'>
      <div className='bg-secondary/5 p-6 border border-border/50 font-mono text-sm space-y-4'>
       <div>
        <span className='text-muted-foreground text-xs uppercase block mb-1'>
         TARGET_ROLE
        </span>
        <h3 className='font-bold text-lg text-primary tracking-wide'>
         {formData.jobTitle}
        </h3>
       </div>
       <div className='grid grid-cols-2 gap-4'>
        <div>
         <span className='text-muted-foreground text-xs uppercase block mb-1'>
          ORGANIZATION
         </span>
         <p className='text-foreground'>{formData.companyName}</p>
        </div>
        <div>
         <span className='text-muted-foreground text-xs uppercase block mb-1'>
          CONFIGURATION
         </span>
         <p className='text-foreground'>
          {formData.experienceLevel} // {formData.interviewType}
         </p>
        </div>
       </div>
      </div>
      <div className='flex space-x-3 pt-4'>
       <Button
        onClick={handleSaveProfile}
        disabled={isLoading}
        className='flex-1 rounded-none font-mono uppercase tracking-widest'
       >
        {isLoading ? 'SAVING...' : 'INITIALIZE_PROFILE'}
       </Button>
       <Button
        variant='outline'
        onClick={() => setStep('edit')}
        disabled={isLoading}
        className='rounded-none font-mono uppercase'
       >
        MODIFY_DATA
       </Button>
      </div>
     </CardContent>
    </Card>
   )}
  </div>
 );
}
